import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { secureStorage } from '../utils/secureStorage';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor — async to support secureStorage on native
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const token = await secureStorage.get('accessToken');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // secureStorage unavailable (e.g. during app init) — proceed without token
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = await secureStorage.get('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data.tokens;
        await secureStorage.set('accessToken', accessToken);
        await secureStorage.set('refreshToken', newRefreshToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed — clear tokens and force the user to the login page.
        await secureStorage.remove('accessToken');
        await secureStorage.remove('refreshToken');
        window.dispatchEvent(new CustomEvent('auth:logout'));
        // The app uses BrowserRouter, so setting location.hash navigates
        // nowhere and strands the user on a dead dashboard where every request
        // silently 401s. A real navigation resets app state cleanly. Guard
        // against a redirect loop when the failing request IS the login flow.
        if (!window.location.pathname.startsWith('/login')) {
          window.location.assign('/login');
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  signup: (data: { email: string; password: string; firstName: string; lastName: string; phone?: string; companyName?: string }) =>
    api.post('/auth/signup', data),
  
  verifyEmail: (data: { email: string; otp: string }) =>
    api.post('/auth/verify-email', data),
  
  resendVerification: (data: { email: string }) =>
    api.post('/auth/resend-verification', data),
  
  login: (data: { email: string; password: string }) =>
    api.post('/auth/login', data),
  
  verify2FA: (data: { tempToken: string; otp: string }) =>
    api.post('/auth/verify-2fa', data),

  resend2FA: (data: { tempToken: string }) =>
    api.post('/auth/resend-2fa', data),
  
  forgotPassword: (data: { email: string }) =>
    api.post('/auth/forgot-password', data),
  
  resetPassword: (data: { email: string; otp: string; newPassword: string }) =>
    api.post('/auth/reset-password', data),
  
  refreshToken: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),
  
  logout: (data?: { allDevices?: boolean }) =>
    api.post('/auth/logout', data),
  
  enable2FA: () =>
    api.post('/auth/2fa/enable'),
  
  confirm2FA: (data: { code: string }) =>
    api.post('/auth/2fa/confirm', data),
  
  disable2FA: (data: { password: string }) =>
    api.post('/auth/2fa/disable', data),
};

// User API
export const userApi = {
  getMe: () => api.get('/users/me'),
  // API keys. `create` is the only call that ever returns the secret — the
  // server stores a hash, so there is no route that could return it twice.
  listApiKeys: () => api.get('/users/api-keys'),
  getApiKeyScopes: () => api.get('/users/api-key-scopes'),
  // The logo is a base64 data URL kept out of GET /me, which runs on every app
  // boot. Fetched only where it is displayed.
  getLogo: () => api.get('/users/me/logo'),
  removeLogo: () => api.delete('/users/me/logo'),
  createApiKey: (data: {
    name: string;
    permissions?: string[];
    expiresInDays?: number;
    dailyCreditCap?: number;
  }) =>
    api.post('/users/api-keys', data),
  revokeApiKey: (id: string) => api.delete(`/users/api-keys/${id}`),
  // OAuth connections — apps the user signed into, as opposed to keys they
  // pasted. Different way in, same tools; listed side by side because a person
  // thinking "what can reach my account" does not care which door was used.
  listConnections: () => api.get('/oauth/connections'),
  revokeConnection: (clientId: string) => api.delete(`/oauth/connections/${clientId}`),
  updateMe: (data: any) => api.patch('/users/me', data),
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.post('/users/change-password', data),
  getNotifications: () => api.get('/users/notifications'),
  markNotificationRead: (id: string) => api.patch(`/users/notifications/${id}/read`, {}),
  markAllNotificationsRead: () => api.patch('/users/notifications/read-all', {}),
  deleteNotification: (id: string) => api.delete(`/users/notifications/${id}`),
  clearAllNotifications: () => api.delete('/users/notifications'),
  getUnreadCount: () => api.get('/users/notifications/unread-count'),
  getApiKeys: () => api.get('/users/api-keys'),
  getInferredPreferences: () => api.get('/users/me/inferred-preferences'),
  confirmPreferences: (data: { naicsCodes?: string[]; agencies?: string[]; setAsides?: string[] }) =>
    api.post('/users/me/confirm-preferences', data),
  getProposalReadiness: () => api.get('/users/me/proposal-readiness'),
  exportMyData: () => api.get('/users/me/export'),
  deleteAccount: (data: { password?: string }) => api.delete('/users/me', { data }),
};

// Subscription API
export const subscriptionApi = {
  getSubscription: () => api.get('/subscriptions'),
  getPlans: () => api.get('/subscriptions/plans'),
  createSetupIntent: () => api.post('/subscriptions/setup-intent', {}),
  createSubscription: (data: { plan: string; paymentMethodId: string; isAnnual?: boolean }) =>
    api.post('/subscriptions', data),
  createCheckoutSession: (data: { plan: string; isAnnual?: boolean }) =>
    api.post('/subscriptions/checkout', data),
  cancelSubscription: (data?: { immediate?: boolean }) =>
    api.post('/subscriptions/cancel', data),
  updateSubscription: (data: { plan: string }) =>
    api.patch('/subscriptions', data),
  getPortalSession: () => api.post('/subscriptions/portal-session', {}),
  syncCheckoutSession: (sessionId: string) =>
    api.post('/subscriptions/sync-checkout', { sessionId }),
};

// Opportunities API
export interface PscOption { code: string; label: string; group: string; openCount: number }
export interface MatchRow {
  alertId: string;
  opportunityId: string;
  samNoticeId: string | null;
  title: string;
  department: string | null;
  agencyPath: string | null;
  naics: string | null;
  setAside: string | null;
  state: string | null;
  type: string | null;
  postedDate: string | null;
  responseDeadline: string | null;
  matchScore: number;
  reasons: string[];
  matchedAt: string;
  dismissed: boolean;
  inPipeline: boolean;
}
export interface MatchList { matches: MatchRow[]; total: number; newCount: number; page: number; limit: number }

export const opportunityApi = {
  search: (params?: any) => api.get('/opportunities/search', { params }),
  /** SAM's own department names, with open-opportunity counts, for the agency filter. */
  // PSC codes present in open notices of the given types, most common first.
  getPscCodes: (ptype?: string) =>
    api.get<{ psc: PscOption[] }>('/opportunities/filters/psc', { params: ptype ? { ptype } : {} }),
  getAgencies: (ptype?: string) =>
    api.get<{ agencies: { name: string; openCount: number; subTiers: { name: string; openCount: number }[] }[] }>('/opportunities/filters/agencies', { params: ptype ? { ptype } : {} }),
  // Matches the matcher recorded against the user's saved criteria.
  getMatches: (params?: { status?: 'new' | 'dismissed' | 'all'; page?: number; limit?: number }) =>
    api.get<MatchList>('/opportunities/matches', { params }),
  dismissMatch: (alertId: string, dismissed = true) => api.post(`/opportunities/matches/${alertId}/dismiss`, { dismissed }),
  getById: (id: string) => api.get(`/opportunities/${id}`),
  getSummary: (id: string) => api.get(`/opportunities/${id}/summary`, { timeout: 120000 }),
  getNLPSummary: (id: string) => api.get(`/opportunities/${id}/nlp-summary`),
  save: (id: string, data: any) => api.post(`/opportunities/${id}/save`, data),
  getRecommendations: () => api.get('/opportunities/recommendations/list'),
};

// AI API
export const aiApi = {
  chat: (data: { message: string; conversationId?: string; context?: any; history?: any[] }) =>
    api.post('/ai/chat', data),
  getConversations: () => api.get('/ai/chat/conversations'),
  getConversation: (id: string) => api.get(`/ai/chat/conversations/${id}`),
  deleteConversation: (id: string) => api.delete(`/ai/chat/conversations/${id}`),
  generateProposal: (data: any) => api.post('/ai/proposal/generate', data, { timeout: 120000 }),
  // Async generation: returns a jobId immediately, then poll getGenerationStatus.
  // First-run generation does deep solicitation analysis and can take several
  // minutes — longer than any single request should be held open.
  generateProposalAsync: (data: any) => api.post('/ai/proposal/generate-async', data),
  getGenerationStatus: (jobId: string) => api.get(`/ai/proposal/generate-async/${jobId}`),
  getProposal: (opportunityId: string) => api.get(`/ai/proposal/${opportunityId}`),
  exportProposal: (proposalId: string, format: string = 'docx') =>
    api.get(`/ai/proposal/${proposalId}/export?format=${format}`, { responseType: 'blob', timeout: 120000 }),
  getRequirements: (opportunityId: string, force?: boolean) => api.get(`/ai/proposal/${opportunityId}/requirements`, { timeout: 120000, params: force ? { force: true } : undefined }),
  analyze: (data: any) => api.post('/ai/analyze', data),
  uploadDocument: (formData: FormData) =>
    api.post('/ai/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  queryDocuments: (data: { query: string }) => api.post('/ai/documents/query', data),
  saveDocumentFromOpportunity: (data: {
    documentUrl: string;
    filename: string;
    noticeId?: string;
    solicitationNumber?: string;
    title?: string;
    agency?: string;
    source?: string;
  }) => api.post('/ai/documents/save-from-opportunity', data),
  getDocuments: () => api.get('/ai/documents'),
  checkOpportunityDocuments: (noticeId: string) =>
    api.get(`/ai/documents/check-opportunity/${noticeId}`),
  deleteDocument: (documentId: string) =>
    api.delete(`/ai/documents/${documentId}`),
  bulkDeleteDocuments: (documentIds: string[]) =>
    api.post('/ai/documents/bulk-delete', { documentIds }),
  // Proposal Checklist
  // Unfilled values in a proposal, split by whether they affect whether the
  // quote can be evaluated at all.
  getProposalGaps: (proposalId: string) => api.get(`/ai/proposal/${proposalId}/gaps`),
  // The agency's own pricing sheet: read its line items, save prices against
  // them, and get the original workbook back with the values written in.
  getBidSchedule: (proposalId: string) => api.get(`/ai/proposal/${proposalId}/bid-schedule`),
  saveBidSchedule: (proposalId: string, prices: Record<string, string | number>) =>
    api.put(`/ai/proposal/${proposalId}/bid-schedule`, { prices }),
  downloadBidSchedule: (proposalId: string) =>
    api.get(`/ai/proposal/${proposalId}/bid-schedule/download`, { responseType: 'blob' }),
  generateChecklist: (opportunityId: string) =>
    api.post(`/ai/proposal/${opportunityId}/checklist/generate`, {}, { timeout: 120000 }),
  getChecklist: (opportunityId: string) =>
    api.get(`/ai/proposal/${opportunityId}/checklist`),
  toggleChecklistItem: (itemId: string) =>
    api.patch(`/ai/proposal/checklist/${itemId}/toggle`),
  updateChecklistValue: (itemId: string, inputValue: string) =>
    api.patch(`/ai/proposal/checklist/${itemId}/value`, { inputValue }),
};

// CRM API
export const crmApi = {
  getDashboard: () => api.get('/crm/dashboard'),
  getContacts: (params?: any) => api.get('/crm/contacts', { params }),
  addContact: (data: any) => api.post('/crm/contacts', data),
  updateContact: (id: string, data: any) => api.patch(`/crm/contacts/${id}`, data),
  deleteContact: (id: string) => api.delete(`/crm/contacts/${id}`),
};

// Integration API
export const integrationApi = {
  getAll: () => api.get('/integrations'),
  connectGSA: (data: { apiKey: string }) => api.post('/integrations/gsa', data),
  connectSeaPort: (data: { credentials: any }) => api.post('/integrations/seaport', data),
  connectZapier: () => api.post('/integrations/zapier', {}),
  connectSalesforce: (data: any) => api.post('/integrations/salesforce', data),
  disconnect: (id: string) => api.delete(`/integrations/${id}`),
};

// Team API
export const teamApi = {
  getAll: () => api.get('/teams'),
  create: (data: any) => api.post('/teams', data),
  inviteMember: (teamId: string, data: { email: string; role: string }) =>
    api.post(`/teams/${teamId}/invite`, data),
  updateMemberRole: (teamId: string, memberId: string, data: { role: string }) =>
    api.patch(`/teams/${teamId}/members/${memberId}/role`, data),
  removeMember: (teamId: string, memberId: string) =>
    api.delete(`/teams/${teamId}/members/${memberId}`),
};

// Agent API (harness)
export const agentApi = {
  // Agent Types
  getTypes: () => api.get('/agents/types'),
  getHealth: () => api.get('/agents/health'),
  
  // Task Management
  createTask: (data: { agent_type: string; task: any; priority?: string }) =>
    api.post('/agents/tasks', data),
  getTask: (id: string) => api.get(`/agents/tasks/${id}`),
  getTaskResult: (id: string) => api.get(`/agents/tasks/${id}/result`),
  cancelTask: (id: string) => api.delete(`/agents/tasks/${id}`),
  getMyTasks: (params?: { status?: string; limit?: number }) =>
    api.get('/agents/tasks', { params }),
  getTasksByOpportunity: (opportunityId: string) =>
    api.get(`/agents/tasks/by-opportunity/${opportunityId}`),
  clearFailedTasks: () =>
    api.delete('/agents/tasks/failed/clear'),
};

// Pipeline API (Opportunity Pipeline)
export interface SavedViewBody {
  v: 1;
  filters: { ptype: string[]; setAside: string[]; agency: string[]; naicsCode: string[]; psc: string[]; state: string[]; includeExpired: boolean; includeUnspecifiedState: boolean };
  searchQuery: string;
  sort: 'posted:desc' | 'posted:asc' | 'due:asc' | 'due:desc';
}
export interface SavedView {
  id: string; name: string; color: string; view: SavedViewBody | null; isDefault: boolean; position: number; updatedAt: string; createdAt: string;
}
// Named filter sets on /opportunities. Views are free on every plan; watching one is Pro.
export const savedViewsApi = {
  list: () => api.get<{ success: boolean; data: { views: SavedView[]; watchedViewId: string | null; watchBehind: boolean; max: number } }>('/saved-views'),
  create: (body: { name: string; color?: string; view: SavedViewBody; isDefault?: boolean }) => api.post('/saved-views', body),
  update: (id: string, body: Partial<{ name: string; color: string; view: SavedViewBody; isDefault: boolean; position: number }>) => api.patch(`/saved-views/${id}`, body),
  remove: (id: string) => api.delete(`/saved-views/${id}`),
  watch: (id: string) => api.post(`/saved-views/${id}/watch`),
};

export const pipelineApi = {
  // Opportunities in Pipeline
  getOpportunities: (params?: { stage?: string; assignedToMe?: boolean }) =>
    api.get('/pipeline/opportunities', { params }),
  getOpportunity: (id: string) => api.get(`/pipeline/opportunities/${id}`),
  getByOpportunityId: (opportunityId: string) => api.get(`/pipeline/opportunities/${opportunityId}`),
  addOpportunity: (opportunityId: string) => api.post(`/pipeline/opportunities/${opportunityId}`, {}),
  updateStage: (id: string, data: { stage: string; notes?: string }) =>
    api.patch(`/pipeline/${id}/stage`, data),
  analyze: (id: string) => api.post(`/pipeline/${id}/analyze`),
  // The pursuit workspace: one read for the whole page. `key` is the pipeline
  // row id, the opportunity UUID or the SAM notice id.
  listPursuits: () => api.get('/pipeline/pursuits'),
  getWorkspace: (key: string, opts?: { touch?: boolean }) => api.get(`/pipeline/workspace/${key}`, { params: opts?.touch ? { touch: 1 } : undefined }),
  getSubmitGate: (key: string) => api.get(`/pipeline/workspace/${key}/submit-gate`),
  getAwardHistory: (key: string) => api.get(`/pipeline/workspace/${key}/awards`),
  submitPursuit: (key: string, body: { force?: boolean; method?: string; recipient?: string | null; sentAt?: string | null; confirmation?: string | null }) => api.post(`/pipeline/workspace/${key}/submit`, body),
  setDebriefDate: (key: string, debriefAt: string | null) => api.patch(`/pipeline/workspace/${key}/debrief`, { debriefAt }),
  retryDocument: (key: string, documentId: string) => api.post(`/pipeline/workspace/${key}/documents/${documentId}/retry`),
  markReviewed: (key: string) => api.patch(`/pipeline/workspace/${key}/reviewed`),
  setOutcome: (key: string, outcome: 'won' | 'lost' | 'missed' | null, note?: string) => api.patch(`/pipeline/workspace/${key}/outcome`, { outcome, note }),
  setOverride: (key: string, proceed: boolean, note?: string) => api.patch(`/pipeline/workspace/${key}/override`, { proceed, note }),
  tickKeyDate: (key: string, rowKey: string, done: boolean) => api.patch(`/pipeline/workspace/${key}/key-dates`, { key: rowKey, done }),
  fetchWorkspaceDocuments: (key: string) => api.post(`/pipeline/workspace/${key}/documents`, {}, { timeout: 60000 }),
  matchVendors: (id: string) => api.post(`/pipeline/opportunities/${id}/match`),
  
  // Configuration
  getConfig: () => api.get('/pipeline/config'),
  updateConfig: (data: any) => api.put('/pipeline/config', data),
  
  // Remove from pipeline
  remove: (pipelineId: string) => api.delete(`/pipeline/${pipelineId}`),

  // Scoring
  calculateMatch: (id: string) => api.post(`/pipeline/opportunities/${id}/calculate-match`),

  // Classification
  getClassification: (opportunityId: string) =>
    api.get(`/pipeline/opportunities/${opportunityId}/classification`),
  suggestClassification: (opportunityId: string) =>
    api.post(`/pipeline/opportunities/${opportunityId}/classification/suggest`, {}, { timeout: 120000 }),
  saveClassification: (opportunityId: string, classification: string) =>
    api.patch(`/pipeline/opportunities/${opportunityId}/classification`, { classification }),
  clearClassification: (opportunityId: string) =>
    api.patch(`/pipeline/opportunities/${opportunityId}/classification`, { classification: '__clear__' }),
  getProfileReadiness: (opportunityId: string) =>
    api.get(`/pipeline/opportunities/${opportunityId}/profile-readiness`),

  // Eligibility (deterministic hard-fail check)
  getEligibility: (opportunityId: string) =>
    api.get(`/pipeline/opportunities/${opportunityId}/eligibility`),

  // Solicitation type classification
  getSolicitationType: (opportunityId: string) =>
    api.get(`/pipeline/opportunities/${opportunityId}/solicitation-type`),
  setSolicitationType: (opportunityId: string, type: string, tier: number) =>
    api.patch(`/pipeline/opportunities/${opportunityId}/solicitation-type`, { type, tier }),
};

// Pricing API — profile + per-proposal items + xlsx export
export const pricingApi = {
  // Profile (per-user singleton)
  getProfile: () => api.get('/pricing/profile'),
  saveProfile: (data: any) => api.put('/pricing/profile', data),

  // Items (per-proposal)
  listItems: (proposalId: string) => api.get(`/pricing/proposals/${proposalId}/items`),
  createItem: (proposalId: string, data: any) =>
    api.post(`/pricing/proposals/${proposalId}/items`, data),
  updateItem: (id: string, data: any) => api.put(`/pricing/items/${id}`, data),
  deleteItem: (id: string) => api.delete(`/pricing/items/${id}`),

  // Compute (read-only with totals)
  compute: (proposalId: string) => api.get(`/pricing/proposals/${proposalId}/compute`),

  // xlsx export — returns absolute URL with auth header (caller adds it)
  exportUrl: (proposalId: string) => `/pricing/proposals/${proposalId}/export`,
};

// Government Forms API
export const governmentFormsApi = {
  list: () => api.get('/government-forms/list'),
  getAll: (opportunityId?: string) =>
    api.get('/government-forms', { params: opportunityId ? { opportunityId } : {} }),
  getOne: (formId: string, opportunityId?: string) =>
    api.get(`/government-forms/${formId}`, { params: opportunityId ? { opportunityId } : {} }),
  // DOCX paths — caller fetches with auth header
  oneDocxUrl: (formId: string, opportunityId?: string) =>
    `/government-forms/${formId}/docx${opportunityId ? `?opportunityId=${opportunityId}` : ''}`,
  bundleDocxUrl: (opportunityId?: string) =>
    `/government-forms/all/docx${opportunityId ? `?opportunityId=${opportunityId}` : ''}`,
};

// Boilerplate Templates API
export const boilerplateApi = {
  list: (params?: { type?: string; activeOnly?: boolean }) =>
    api.get('/boilerplate', { params }),
  get: (id: string) => api.get(`/boilerplate/${id}`),
  create: (data: any) => api.post('/boilerplate', data),
  update: (id: string, data: any) => api.put(`/boilerplate/${id}`, data),
  remove: (id: string) => api.delete(`/boilerplate/${id}`),
};

// Past Performance Library API
export const pastPerformanceApi = {
  list: (params?: { activeOnly?: boolean; limit?: number; offset?: number }) =>
    api.get('/past-performance', { params }),
  get: (id: string) => api.get(`/past-performance/${id}`),
  create: (data: any) => api.post('/past-performance', data),
  update: (id: string, data: any) => api.put(`/past-performance/${id}`, data),
  remove: (id: string) => api.delete(`/past-performance/${id}`),
};

// Compliance Matrix API
export const complianceMatrixApi = {
  get: (proposalId: string) => api.get(`/proposals/${proposalId}/compliance-matrix`),
  updateItem: (id: string, data: any) => api.patch(`/compliance-matrix/${id}`, data),
  addItem: (proposalId: string, data: any) =>
    api.post(`/proposals/${proposalId}/compliance-matrix`, data),
  removeItem: (id: string) => api.delete(`/compliance-matrix/${id}`),
};

// Proposal Review API
export const proposalReviewApi = {
  review: (proposalId: string, sectionId?: string) =>
    api.post('/ai/proposal/review', { proposalId, sectionId }),
};

// Waitlist API (public — no auth required)
export const waitlistApi = {
  join: (email: string, source?: string) =>
    api.post('/waitlist', { email, source }),
};

// Watch List API
export const watchListApi = {
  // Get user's watch list
  getAll: (params?: { limit?: number; offset?: number }) =>
    api.get('/watchlist', { params }),
  
  // Get watch list stats
  getStats: () => api.get('/watchlist/stats'),
  
  // Check if opportunity is in watch list
  checkStatus: (opportunityId: string) =>
    api.get(`/watchlist/opportunities/${opportunityId}/check`),
  
  // Add opportunity to watch list
  add: (opportunityId: string, data?: { notes?: string; alertOnUpdate?: boolean }) =>
    api.post(`/watchlist/opportunities/${opportunityId}`, data || {}),
  
  // Remove opportunity from watch list
  remove: (opportunityId: string) =>
    api.delete(`/watchlist/opportunities/${opportunityId}`),
  
  // Update watch list item
  update: (watchListId: string, data: { notes?: string; alertOnUpdate?: boolean }) =>
    api.patch(`/watchlist/${watchListId}`, data),
  
  // Toggle alerts
  toggleAlerts: (opportunityId: string, enabled: boolean) =>
    api.post(`/watchlist/opportunities/${opportunityId}/alerts`, { enabled }),
};

// Documents API
export const documentApi = {
  // Get filename for a single document URL
  getFilename: (url: string) =>
    api.get('/documents/filename', { params: { url } }),
  
  // Get filenames for multiple document URLs (batch)
  getFilenames: (urls: string[]) =>
    api.post('/documents/filenames', { urls }),
};

// Orchestrator API (Workflows)
export const orchestratorApi = {
  // Workflows
  getWorkflows: () => api.get('/orchestrator/workflows'),
  createWorkflow: (data: { name: string; description?: string; definition: any }) =>
    api.post('/orchestrator/workflows', data),
  getWorkflow: (id: string) => api.get(`/orchestrator/workflows/${id}`),
  executeWorkflow: (id: string, context?: any) =>
    api.post(`/orchestrator/workflows/${id}/execute`, { context }),
  deleteWorkflow: (id: string) => api.delete(`/orchestrator/workflows/${id}`),
  
  // Agent Chains
  createChain: (data: { name: string; description?: string; steps: any[] }) =>
    api.post('/orchestrator/chains', data),
  executeChain: (id: string, initialInput?: any) =>
    api.post(`/orchestrator/chains/${id}/execute`, { initialInput }),
  
  // Executions
  getExecution: (id: string) => api.get(`/orchestrator/executions/${id}`),
  getExecutions: (params?: { workflowId?: string; status?: string }) =>
    api.get('/orchestrator/executions', { params }),
};

// Analytics API
export const analyticsApi = {
  getDashboard: () => api.get('/analytics/dashboard'),
  getAgentPerformance: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/analytics/agents/performance', { params }),
  getOpportunityReport: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/analytics/reports/opportunities', { params }),
  getAgentReport: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/analytics/reports/agents', { params }),
  exportReport: (type: string, format: 'csv' | 'json', params?: any) =>
    api.get('/analytics/reports/export', {
      params: { type, format, ...params },
      responseType: format === 'csv' ? 'blob' : 'json'
    }),
  track: (action: string, metadata?: Record<string, any>) =>
    api.post('/analytics/track', { action, metadata }).catch(() => {}),
};

// Collaboration API
export const collaborationApi = {
  // Comments
  getComments: (opportunityId: string) =>
    api.get(`/collaboration/opportunities/${opportunityId}/comments`),
  addComment: (opportunityId: string, data: { content: string; parentId?: string; mentions?: string[] }) =>
    api.post(`/collaboration/opportunities/${opportunityId}/comments`, data),
  resolveComment: (opportunityId: string, commentId: string) =>
    api.post(`/collaboration/opportunities/${opportunityId}/comments/${commentId}/resolve`),
  
  // Assignments
  assignOpportunity: (opportunityId: string, data: { userId: string; role: string }) =>
    api.post(`/collaboration/opportunities/${opportunityId}/assign`, data),
  unassignOpportunity: (opportunityId: string, userId: string) =>
    api.delete(`/collaboration/opportunities/${opportunityId}/assign/${userId}`),
  getMyAssignments: () => api.get('/collaboration/assigned'),
  
  // Activity Feed
  getActivityFeed: (opportunityId: string, params?: { limit?: number; before?: string }) =>
    api.get(`/collaboration/opportunities/${opportunityId}/activity`, { params }),
  
  // Shared Opportunities
  getSharedOpportunities: () => api.get('/collaboration/shared'),
};

// Email API
export const emailApi = {
  createRelay: (data: { name: string; purpose?: string; forwardTo?: string }) =>
    api.post('/emails/relay', data),
  getInbox: (params?: { status?: string; limit?: number }) =>
    api.get('/emails/inbox', { params }),
  getEmail: (id: string) => api.get(`/emails/inbox/${id}`),
};

export default api;
